import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { FALLBACK_CHF_TO_EUR_RATE } from "../../../constants/pricing";
import { exchangeRateSessionSlice, useEphemeralSelector } from "../../../store/ephemeral";
import { pricingPrefsSlice, usePersistedSlice, usePersistedSliceSelector } from "../../../store/persistence";
import { getUIColorPalette } from "../../../theme/ColorPalette";

interface CarPriceDetailProps {
    "isOpen":    boolean;
    "onClose":   () => void;
    "tollPrice": number;
    "distance":  number;
    "isEuro"?:   boolean;
    "isDarkUi"?: boolean;
}

type CarPriceDetailContentProps = Omit<CarPriceDetailProps, "isOpen"> & {

    /** Stored wear cost, always CHF/km */
    "initialWearCostPerKmChf": number;
    "isEuro":                  boolean;
    "isDarkUi":                boolean;
    "exchangeRate":            number;
    "onWearCommittedChf":      ( wearCostPerKmChf: number ) => void;
};

const formatDisplayInputString = ( displayAmount: number ): string => {
    const rounded = Math.round( displayAmount * 1000 ) / 1000;
    if ( Number.isInteger( rounded ) ) {
        return String( rounded );
    }

    return rounded.toFixed( 3 ).replace( /\.?0+$/, "" );
};

/**
 * Inner component that manages the modal content and input state.
 * Mounts fresh each time the modal opens, so inputValue initializes correctly.
 */
const CarPriceDetailContent: React.FC<CarPriceDetailContentProps> = ( {
    onClose,
    tollPrice,
    distance,
    onWearCommittedChf,
    initialWearCostPerKmChf,
    isEuro,
    isDarkUi,
    exchangeRate,
} ) => {
    const [ wearCostPerKmChf, setWearCostPerKmChf ] = useState<number>( initialWearCostPerKmChf );
    const [ inputValue, setInputValue ] = useState<string>( () => {
        const display = isEuro ? initialWearCostPerKmChf * exchangeRate : initialWearCostPerKmChf;

        return formatDisplayInputString( display );
    } );

    const currency = isEuro ? "€" : "CHF";
    const palette = getUIColorPalette( isDarkUi );

    const displayTollPrice = isEuro ? tollPrice * exchangeRate : tollPrice;
    const displayWearCostPerKm = isEuro ? wearCostPerKmChf * exchangeRate : wearCostPerKmChf;
    const totalWearCostChf = distance * wearCostPerKmChf;
    const displayTotalWearCost = isEuro ? totalWearCostChf * exchangeRate : totalWearCostChf;
    const totalPriceChf = tollPrice + totalWearCostChf;
    const displayTotalPrice = isEuro ? totalPriceChf * exchangeRate : totalPriceChf;

    const displayAmountToChfPerKm = useCallback( ( displayAmount: number ): number => {
        if ( isEuro ) {
            return exchangeRate > 0 ? displayAmount / exchangeRate : displayAmount;
        }

        return displayAmount;
    }, [ exchangeRate, isEuro ] );

    const handleInputChange = useCallback( ( e: React.ChangeEvent<HTMLInputElement> ) => {
        const value = e.target.value;
        setInputValue( value );

        const numericValue = parseFloat( value );
        if ( !isNaN( numericValue ) && numericValue >= 0 ) {
            const chfPerKm = displayAmountToChfPerKm( numericValue );
            setWearCostPerKmChf( chfPerKm );
            onWearCommittedChf( chfPerKm );
        }
    }, [ displayAmountToChfPerKm, onWearCommittedChf ] );

    const handleInputBlur = useCallback( () => {
        const numericValue = parseFloat( inputValue );
        if ( isNaN( numericValue ) || numericValue < 0 ) {
            setInputValue( formatDisplayInputString( isEuro ? wearCostPerKmChf * exchangeRate : wearCostPerKmChf ) );
        }
    }, [ exchangeRate, inputValue, isEuro, wearCostPerKmChf ] );

    /*
     * Portal content still participates in React’s bubbling tree; without stopPropagation,
     * close clicks would reach MyCarProposition’s row onClick and reopen the car itinerary view.
     */
    const handleBackdropClick = useCallback( ( event: React.MouseEvent<HTMLDivElement> ) => {
        event.stopPropagation();
        onClose();
    }, [ onClose ] );

    const handleDialogClick = useCallback( ( event: React.MouseEvent<HTMLDivElement> ) => {
        event.stopPropagation();
    }, [] );

    const handleCloseButtonClick = useCallback( ( event: React.MouseEvent<HTMLButtonElement> ) => {
        event.stopPropagation();
        onClose();
    }, [ onClose ] );

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            onClick={handleBackdropClick}
            role="presentation"
            style={{ "zIndex": 999999 }}
        >
            <div
                className="rounded-lg p-6 m-4 max-w-md w-full shadow-xl"
                onClick={handleDialogClick}
                role="dialog"
                style={{
                    "backgroundColor": palette.modalCardBackground,
                    "color":           palette.modalCardTextPrimary,
                    "border":          `1px solid ${ palette.modalCardBorder }`,
                }}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold">
                        Détails du Prix
                    </h2>
                    <button
                        className="text-2xl font-bold"
                        onClick={handleCloseButtonClick}
                        style={{ "color": palette.overlayTextSecondary }}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <div className="space-y-4">
                    <div
                        className="flex justify-between items-center py-3 border-b"
                        style={{ "borderColor": palette.overlayBorder }}
                    >
                        <span
                            className="font-medium"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            Prix du péage
                        </span>
                        <span
                            className="text-lg font-bold"
                            style={{ "color": "var(--itinerary-brand-accent, #1bd98d)" }}
                        >
                            {displayTollPrice.toFixed( 2 )}
                            {" "}
                            {currency}
                        </span>
                    </div>
                    <div
                        className="flex justify-between items-center py-3 border-b"
                        style={{ "borderColor": palette.overlayBorder }}
                    >
                        <span
                            className="font-medium"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            Distance
                        </span>
                        <span className="text-lg font-bold">
                            {distance.toFixed( 2 )}
                            {" "}
                            km
                        </span>
                    </div>
                    <div
                        className="py-3 border-b"
                        style={{ "borderColor": palette.overlayBorder }}
                    >
                        <div className="flex justify-between items-center mb-2">
                            <span
                                className="font-medium"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                Coût d usure par km
                            </span>
                            <div className="flex items-center space-x-2">
                                <input
                                    className="w-24 px-2 py-1 border rounded text-right font-bold"
                                    min="0"
                                    onBlur={handleInputBlur}
                                    onChange={handleInputChange}
                                    step={isEuro ? "0.01" : "0.1"}
                                    style={{
                                        "borderColor":     palette.overlayBorder,
                                        "backgroundColor": palette.overlayCardBackgroundSoft,
                                        "color":           palette.overlayTextPrimary,
                                    }}
                                    type="number"
                                    value={inputValue}
                                />
                                <span>
                                    {currency}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        className="flex justify-between items-center py-3 border-b"
                        style={{ "borderColor": palette.overlayBorder }}
                    >
                        <span
                            className="font-medium"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            Coût d usure total
                            <br />
                            <small style={{ "color": palette.overlayTextSecondary }}>
                                (
                                {distance.toFixed( 2 )}
                                {" "}
                                km ×
                                {displayWearCostPerKm.toFixed( 2 )}
                                {" "}
                                {currency}
                                )
                            </small>
                        </span>
                        <span className="text-lg font-bold text-red-500">
                            {displayTotalWearCost.toFixed( 2 )}
                            {" "}
                            {currency}
                        </span>
                    </div>
                    <div
                        className="flex justify-between items-center py-4 rounded-lg px-4"
                        style={{ "backgroundColor": palette.overlayCardBackgroundSoft }}
                    >
                        <span className="font-bold text-lg">
                            Prix Total
                        </span>
                        <span className="text-xl font-bold">
                            {displayTotalPrice.toFixed( 2 )}
{" "}
{currency}
                        </span>
                    </div>

                </div>

            </div>
        </div>,
        document.body,
    );
};

const CarPriceDetail: React.FC<CarPriceDetailProps> = ( {
    isOpen,
    onClose,
    tollPrice,
    distance,
    isEuro = false,
    isDarkUi = false,
} ) => {
    const wearCostPerKmChf = usePersistedSliceSelector(
        pricingPrefsSlice,
        ( prefs ) => prefs.wearCostPerKmChf,
    );
    const [ , setPricingPrefs ] = usePersistedSlice( pricingPrefsSlice );
    const chfToEurRate = useEphemeralSelector( exchangeRateSessionSlice, ( session ) => session.chfToEurRate );
    const exchangeRate = chfToEurRate !== null && chfToEurRate > 0 ? chfToEurRate : FALLBACK_CHF_TO_EUR_RATE;

    if ( !isOpen ) {
        return null;
    }

    return (
        <CarPriceDetailContent
            distance={distance}
            exchangeRate={exchangeRate}
            initialWearCostPerKmChf={wearCostPerKmChf}
            isDarkUi={isDarkUi}
            isEuro={isEuro}
            onClose={onClose}
            onWearCommittedChf={( wearCostPerKmChfCommitted ) => {
                setPricingPrefs( ( previous ) => ( {
                    ...previous,
                    "wearCostPerKmChf": wearCostPerKmChfCommitted,
                } ) );
            }}
            tollPrice={tollPrice}
        />
    );
};

export default CarPriceDetail;
